// /admin/ads — FBZipper's Facebook ads end to end: the creative library (reels + videos of
// past ads), A/B tests of creatives × flows, every ad's spend → signups → connected profiles →
// paying customers → revenue, the WhatsApp inbox and the editable signup flow. Signups are attributed via utm_content = Meta ad id
// (api lib/adAttribution.js). The old public /ads gallery redirects here.
import {FC, useCallback, useEffect, useState} from 'react'
import {
  AdSignup,
  AudienceInfo,
  FbLeadRow,
  getAudiences,
  getLeads,
  importExistingAds,
  pollLeads,
  syncAudiences,
  getLaunches,
  getOverview,
  getReels,
  getSignups,
  Launch,
  Overview,
  removeLaunch,
  setLaunchBudget,
  setLaunchStatus,
} from './_requests'
import FlowEditor from './FlowEditor'
import WaInbox from './WaInbox'
import HowItWorks from './HowItWorks'
import Creatives from './Creatives'
import AbTests from './AbTests'
import AdsGrid from './AdsGrid'
import LaunchTest from './LaunchTest'
import Stuck from './Stuck'

const ils = (n: number | string | undefined) =>
  `₪${Math.round(Number(n || 0)).toLocaleString('he-IL')}`
const num = (n: number | string | undefined) => Number(n || 0).toLocaleString('he-IL')
const errMsg = (e: any) => e?.response?.data?.error || e?.message || 'שגיאה'

const ACCOUNT_STATUS: Record<number, {label: string; cls: string}> = {
  1: {label: 'פעיל', cls: 'success'},
  2: {label: 'מושבת', cls: 'danger'},
  3: {label: 'חוב לא שולם — המודעות לא רצות', cls: 'danger'},
  7: {label: 'בבדיקת סיכון', cls: 'warning'},
  9: {label: 'בתקופת חסד', cls: 'warning'},
}

const card = (bg: string): React.CSSProperties => ({
  background: bg,
  borderRadius: 12,
  padding: '16px 18px',
  color: '#fff',
  flex: '1 1 170px',
  minWidth: 170,
})

const AUDIENCE_LABEL: Record<string, string> = {
  payers: 'לקוחות משלמים (מקור ל-Lookalike)',
  accounts: 'כל החשבונות (מוחרגים מהמודעות)',
  lal_1: 'Lookalike 1% ישראל',
  lal_3: 'Lookalike 3% ישראל',
}

const DEST_LABEL: Record<string, string> = {
  website: 'דף הרשמה',
  lead_form: 'טופס פייסבוק',
  whatsapp: 'פתיחת וואטסאפ',
  other: 'אחר',
}

const AdsPage: FC = () => {
  const [overview, setOverview] = useState<Overview | null>(null)
  const [configured, setConfigured] = useState(true)
  const [defaultLink, setDefaultLink] = useState('')
  const [launches, setLaunches] = useState<Launch[]>([])
  const [signups, setSignups] = useState<AdSignup[]>([])
  const [error, setError] = useState('')
  const [rowBusy, setRowBusy] = useState<string | null>(null)
  const [audiences, setAudiences] = useState<Record<string, AudienceInfo>>({})
  const [audSyncedAt, setAudSyncedAt] = useState<string | null>(null)
  const [audBusy, setAudBusy] = useState(false)
  const [leads, setLeads] = useState<FbLeadRow[]>([])
  const [leadBusy, setLeadBusy] = useState(false)
  type Tab = 'launch' | 'ads' | 'creatives' | 'tests' | 'stuck' | 'how' | 'wa' | 'flow'
  const [tab, setTab] = useState<Tab>(() => {
    const h = window.location.hash.replace('#', '')
    return ['launch', 'flow', 'wa', 'how', 'creatives', 'tests', 'stuck'].includes(h)
      ? (h as Tab)
      : 'ads'
  })
  const switchTab = (t: Tab) => {
    setTab(t)
    window.history.replaceState(null, '', t === 'ads' ? window.location.pathname : '#' + t)
  }
  const [selectedCreatives, setSelectedCreatives] = useState<string[]>([])
  const [adFilter, setAdFilter] = useState<'all' | 'created' | 'imported' | 'active'>('all')
  const [importBusy, setImportBusy] = useState(false)
  const [importMsg, setImportMsg] = useState('')
  const runImport = async () => {
    setImportBusy(true)
    setImportMsg('')
    try {
      const {data} = await importExistingAds()
      setImportMsg(
        `נמצאו ${data.total} מודעות שיווק בחשבון · נוספו ${data.added} · עודכנו ${data.updated}` +
          (data.skipped_hiring ? ` · ${data.skipped_hiring} מודעות גיוס עובדים דולגו` : '') +
          (data.forms && data.forms.forms_added
            ? ` · ${data.forms.forms_added} טפסי לידים חוברו (לידים חדשים בלבד)`
            : '')
      )
      await loadLaunches()
    } catch (e) {
      setImportMsg(errMsg(e))
    } finally {
      setImportBusy(false)
    }
  }

  const loadLaunches = useCallback(async () => {
    try {
      const {data} = await getLaunches()
      setLaunches(data.launches)
    } catch (e) {
      setError(errMsg(e))
    }
  }, [])

  const loadAll = useCallback(async () => {
    setError('')
    getOverview()
      .then(({data}) => {
        setConfigured(data.configured)
        setOverview(data.overview || null)
      })
      .catch((e) => setError(errMsg(e)))
    getReels().then(({data}) => {
      setDefaultLink(data.default_link)
    })
    getSignups().then(({data}) => setSignups(data.signups))
    getAudiences()
      .then(({data}) => {
        setAudiences(data.audiences)
        setAudSyncedAt(data.last_synced_at)
      })
      .catch(() => {})
    getLeads()
      .then(({data}) => setLeads(data.leads))
      .catch(() => {})
    loadLaunches()
  }, [loadLaunches])

  useEffect(() => {
    loadAll()
  }, [loadAll])

  // A launch uploads the video and waits for Meta to process it — poll until it settles.
  useEffect(() => {
    if (!launches.some((l) => l.status === 'creating')) return
    const t = setTimeout(loadLaunches, 8000)
    return () => clearTimeout(t)
  }, [launches, loadLaunches])

  const toggle = async (l: Launch) => {
    const next = l.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE'
    if (
      next === 'ACTIVE' &&
      !window.confirm(`להפעיל את "${l.name}" בתקציב ${ils(l.daily_budget)} ליום?`)
    )
      return
    setRowBusy(l._id)
    try {
      await setLaunchStatus(l._id, next)
      await loadLaunches()
    } catch (e) {
      setError(errMsg(e))
    } finally {
      setRowBusy(null)
    }
  }

  const changeBudget = async (l: Launch) => {
    const v = window.prompt('תקציב יומי חדש ב-₪', String(l.daily_budget))
    if (!v) return
    setRowBusy(l._id)
    try {
      await setLaunchBudget(l._id, Number(v))
      await loadLaunches()
    } catch (e) {
      setError(errMsg(e))
    } finally {
      setRowBusy(null)
    }
  }

  const remove = async (l: Launch) => {
    setRowBusy(l._id)
    try {
      await removeLaunch(l._id)
      await loadLaunches()
    } catch (e) {
      setError(errMsg(e))
    } finally {
      setRowBusy(null)
    }
  }

  const runAudienceSync = async () => {
    setAudBusy(true)
    setError('')
    try {
      await syncAudiences()
      const {data} = await getAudiences()
      setAudiences(data.audiences)
      setAudSyncedAt(data.last_synced_at)
    } catch (e) {
      setError(errMsg(e))
    } finally {
      setAudBusy(false)
    }
  }

  const runLeadPoll = async () => {
    setLeadBusy(true)
    setError('')
    try {
      await pollLeads()
      const {data} = await getLeads()
      setLeads(data.leads)
    } catch (e) {
      setError(errMsg(e))
    } finally {
      setLeadBusy(false)
    }
  }

  const lookalikes = ['lal_1', 'lal_3'].filter((k) => audiences[k] && !audiences[k].error)

  const st = overview
    ? ACCOUNT_STATUS[overview.account_status] || {
        label: `סטטוס ${overview.account_status}`,
        cls: 'warning',
      }
    : null

  return (
    <div style={{padding: 24, direction: 'rtl'}}>
      <div className='d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4'>
        <div>
          <h2 className='fw-bold mb-1'>📣 מודעות פייסבוק — רילס</h2>
          <div className='text-muted fs-7'>
            יוצרים מודעה מרילס, מפעילים, ועוקבים: הוצאה → הרשמות → חיבור פרופיל → לקוח משלם
          </div>
        </div>
        <button className='btn btn-sm btn-light' onClick={loadAll}>
          <i className='bi bi-arrow-clockwise me-1' /> רענן
        </button>
      </div>

      <ul className='nav nav-tabs nav-line-tabs mb-5 fs-6'>
        <li className='nav-item'>
          <a
            href='#launch'
            className={`nav-link fw-bold ${tab === 'launch' ? 'active' : ''}`}
            onClick={(e) => {
              e.preventDefault()
              switchTab('launch')
            }}
          >
            🚀 השקת בדיקה
          </a>
        </li>
        <li className='nav-item'>
          <a
            href='#ads'
            className={`nav-link ${tab === 'ads' ? 'active' : ''}`}
            onClick={(e) => {
              e.preventDefault()
              switchTab('ads')
            }}
          >
            מודעות
          </a>
        </li>
        <li className='nav-item'>
          <a
            href='#creatives'
            className={`nav-link ${tab === 'creatives' ? 'active' : ''}`}
            onClick={(e) => {
              e.preventDefault()
              switchTab('creatives')
            }}
          >
            קריאייטיבים
          </a>
        </li>
        <li className='nav-item'>
          <a
            href='#tests'
            className={`nav-link ${tab === 'tests' ? 'active' : ''}`}
            onClick={(e) => {
              e.preventDefault()
              switchTab('tests')
            }}
          >
            בדיקות A/B{selectedCreatives.length ? ` (${selectedCreatives.length})` : ''}
          </a>
        </li>
        <li className='nav-item'>
          <a
            href='#stuck'
            className={`nav-link ${tab === 'stuck' ? 'active' : ''}`}
            onClick={(e) => {
              e.preventDefault()
              switchTab('stuck')
            }}
          >
            איפה נתקעים
          </a>
        </li>
        <li className='nav-item'>
          <a
            href='#how'
            className={`nav-link ${tab === 'how' ? 'active' : ''}`}
            onClick={(e) => {
              e.preventDefault()
              switchTab('how')
            }}
          >
            איך זה עובד + השוואה
          </a>
        </li>
        <li className='nav-item'>
          <a
            href='#flow'
            className={`nav-link ${tab === 'flow' ? 'active' : ''}`}
            onClick={(e) => {
              e.preventDefault()
              switchTab('flow')
            }}
          >
            תהליך הרשמה
          </a>
        </li>
        <li className='nav-item'>
          <a
            href='#wa'
            className={`nav-link ${tab === 'wa' ? 'active' : ''}`}
            onClick={(e) => {
              e.preventDefault()
              switchTab('wa')
            }}
          >
            וואטסאפ
          </a>
        </li>
      </ul>

      {tab === 'launch' ? (
        <LaunchTest
          lookalikes={lookalikes}
          onCreated={loadLaunches}
          goTests={() => switchTab('tests')}
        />
      ) : tab === 'creatives' ? (
        <Creatives
          selected={selectedCreatives}
          setSelected={setSelectedCreatives}
          defaultLink={defaultLink}
          lookalikes={lookalikes}
          onLaunched={() => {
            loadLaunches()
            switchTab('ads')
          }}
          goTests={() => switchTab('tests')}
        />
      ) : tab === 'tests' ? (
        <AbTests
          selected={selectedCreatives}
          setSelected={setSelectedCreatives}
          launches={launches}
          lookalikes={lookalikes}
          onCreated={loadLaunches}
          goCreatives={() => switchTab('creatives')}
        />
      ) : tab === 'stuck' ? (
        <Stuck />
      ) : tab === 'how' ? (
        <HowItWorks launches={launches} />
      ) : tab === 'flow' ? (
        <FlowEditor />
      ) : tab === 'wa' ? (
        <WaInbox />
      ) : (
        <>
          {error && <div className='alert alert-danger'>{error}</div>}
          {!configured && (
            <div className='alert alert-warning'>
              חסרים FB_ADS_ACCESS_TOKEN / FB_ADS_ACCOUNT_ID / FB_ADS_PAGE_ID ב-.env של ה-API.
            </div>
          )}

          {overview && st && (
            <div className='d-flex gap-3 flex-wrap mb-4'>
              <div
                style={card(
                  st.cls === 'success'
                    ? 'linear-gradient(135deg,#16a34a,#15803d)'
                    : 'linear-gradient(135deg,#dc2626,#991b1b)'
                )}
              >
                <div className='fs-4 fw-bold'>{st.label}</div>
                <div className='fs-8 opacity-75'>חשבון מודעות {overview.account_id}</div>
              </div>
              <div style={card('linear-gradient(135deg,#2b70fa,#0d65d9)')}>
                <div className='fs-2 fw-bold'>{ils(overview.today.spend)}</div>
                <div className='fs-8 opacity-75'>
                  הוצאה היום · {num(overview.today.clicks)} קליקים
                </div>
              </div>
              <div style={card('linear-gradient(135deg,#7c3aed,#5b21b6)')}>
                <div className='fs-2 fw-bold'>{ils(overview.last_7d.spend)}</div>
                <div className='fs-8 opacity-75'>
                  7 ימים · {num(overview.last_7d.impressions)} חשיפות
                </div>
              </div>
              <div style={card('linear-gradient(135deg,#475569,#1e293b)')}>
                <div className='fs-6 fw-bold'>
                  {overview.pixel_id ? `פיקסל ${overview.pixel_id}` : 'אין פיקסל'}
                </div>
                <div className='fs-8 opacity-75'>
                  דיווח הרשמות לפייסבוק (CAPI): {overview.capi_enabled ? 'פעיל' : 'כבוי בשרת הזה'}
                </div>
              </div>
            </div>
          )}

          <div className='card card-bordered p-4 mb-5'>
            <div className='d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3'>
              <div>
                <h4 className='fw-bold mb-1'>קהלים</h4>
                <div className='text-muted fs-8'>
                  רשימות מוצפנות (hash) של הלקוחות, מתעדכנות פעם ביום
                  {audSyncedAt
                    ? ` · עודכן ${new Date(audSyncedAt).toLocaleString('he-IL')}`
                    : ' · עוד לא סונכרנו'}
                </div>
              </div>
              <button
                className='btn btn-sm btn-light-primary'
                disabled={audBusy}
                onClick={runAudienceSync}
              >
                {audBusy ? 'מסנכרן…' : 'סנכרן עכשיו'}
              </button>
            </div>
            {Object.keys(AUDIENCE_LABEL).map((k) => {
              const a = audiences[k]
              return (
                <div key={k} className='d-flex justify-content-between border-bottom py-2 fs-7'>
                  <span>{AUDIENCE_LABEL[k]}</span>
                  <span className='text-muted'>
                    {!a
                      ? 'לא נוצר'
                      : a.error
                      ? a.error
                      : a.approximate_count_lower_bound && a.approximate_count_lower_bound > 0
                      ? `~${num(a.approximate_count_lower_bound)}–${num(
                          a.approximate_count_upper_bound
                        )} אנשים`
                      : a.operation_status?.description || 'בעיבוד'}
                  </span>
                </div>
              )
            })}
          </div>

          <div className='d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3'>
            <h4 className='fw-bold mb-0'>מודעות</h4>
            <div className='d-flex gap-2 align-items-center flex-wrap'>
              <div className='btn-group btn-group-sm'>
                {(
                  [
                    ['all', 'הכל'],
                    ['active', 'פעילות'],
                    ['created', 'נוצרו כאן'],
                    ['imported', 'קיימות'],
                  ] as const
                ).map(([k, label]) => (
                  <button
                    key={k}
                    className={`btn ${adFilter === k ? 'btn-primary' : 'btn-light'}`}
                    onClick={() => setAdFilter(k)}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <button
                className='btn btn-sm btn-light-primary'
                disabled={importBusy}
                onClick={runImport}
              >
                {importBusy ? 'מייבא…' : 'ייבוא מודעות קיימות מהחשבון'}
              </button>
            </div>
          </div>
          {importMsg && <div className='alert alert-info py-2 fs-7'>{importMsg}</div>}
          {launches.length === 0 ? (
            <div className='text-muted mb-4'>
              עוד אין מודעות. צרו מודעה מלשונית קריאייטיבים, או ייבאו את המודעות שכבר קיימות בחשבון.
            </div>
          ) : (
            <AdsGrid
              launches={launches.filter((l) =>
                adFilter === 'all'
                  ? true
                  : adFilter === 'active'
                  ? l.status === 'ACTIVE'
                  : (l.source || 'created') === adFilter
              )}
              selected={selectedCreatives}
              setSelected={setSelectedCreatives}
              rowBusy={rowBusy}
              onToggle={toggle}
              onBudget={changeBudget}
              onRemove={remove}
              defaultLink={defaultLink}
              lookalikes={lookalikes}
              onLaunched={loadLaunches}
              goTests={() => switchTab('tests')}
            />
          )}

          <div className='card card-bordered p-4 mb-5 d-flex flex-row align-items-center justify-content-between flex-wrap gap-2'>
            <div className='fs-7'>
              <b>רוצים מודעה חדשה?</b> כל הסרטונים — הרילס והסרטונים מהמודעות הקודמות, עם התוצאות
              שלהם — בלשונית קריאייטיבים.
            </div>
            <div className='d-flex gap-2'>
              <button className='btn btn-sm btn-primary' onClick={() => switchTab('creatives')}>
                לקריאייטיבים
              </button>
              <button className='btn btn-sm btn-light-primary' onClick={() => switchTab('tests')}>
                לבדיקות A/B
              </button>
            </div>
          </div>

          <div className='d-flex justify-content-between align-items-center mb-1'>
            <h4 className='fw-bold mb-0'>לידים מטופס פייסבוק</h4>
            <button className='btn btn-sm btn-light' disabled={leadBusy} onClick={runLeadPoll}>
              {leadBusy ? 'בודק…' : 'בדוק לידים חדשים'}
            </button>
          </div>
          <div className='text-muted fs-7 mb-3'>
            כל ליד הופך אוטומטית לחשבון ניסיון ומקבל בוואטסאפ סיסמה וקישור כניסה.
          </div>
          {leads.length === 0 ? (
            <div className='text-muted mb-5'>עוד אין לידים מטופס.</div>
          ) : (
            <div className='table-responsive mb-5'>
              <table className='table table-sm table-row-bordered align-middle fs-7'>
                <thead>
                  <tr className='fw-bold text-muted'>
                    <th>שם</th>
                    <th>טלפון</th>
                    <th>אימייל</th>
                    <th>מתי</th>
                    <th>תוצאה</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l) => (
                    <tr key={l._id}>
                      <td>
                        {l.account ? <a href={`/admin/accounts/${l.account}`}>{l.name}</a> : l.name}
                      </td>
                      <td dir='ltr'>{l.phone}</td>
                      <td>{l.email}</td>
                      <td>{new Date(l.lead_created_at).toLocaleString('he-IL')}</td>
                      <td>
                        {l.status === 'created' ? (
                          <span className='badge badge-light-success'>חשבון נפתח</span>
                        ) : l.status === 'existing' ? (
                          <span className='badge badge-light-info'>כבר לקוח</span>
                        ) : (
                          <span className='badge badge-light-danger' title={l.error}>
                            נכשל
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h4 className='fw-bold mb-1'>נרשמים ממודעות</h4>
          <div className='text-muted fs-7 mb-3'>
            להתקשר / לשלוח וואטסאפ בזמן תקופת הניסיון — מי שעוד לא חיבר פרופיל קודם.
          </div>
          {signups.length === 0 ? (
            <div className='text-muted'>עוד אין נרשמים עם מקור מודעה.</div>
          ) : (
            <div className='table-responsive'>
              <table className='table table-sm table-row-bordered align-middle fs-7'>
                <thead>
                  <tr className='fw-bold text-muted'>
                    <th>שם</th>
                    <th>טלפון</th>
                    <th>נרשם</th>
                    <th>ניסיון עד</th>
                    <th>מודעה</th>
                    <th>חיבר פרופיל</th>
                    <th>שילם</th>
                  </tr>
                </thead>
                <tbody>
                  {signups.map((s) => (
                    <tr key={s._id}>
                      <td>
                        <a href={`/admin/accounts/${s._id}`}>{s.name || s.email}</a>
                      </td>
                      <td dir='ltr'>{s.phone}</td>
                      <td>{new Date(s.created_at).toLocaleDateString('he-IL')}</td>
                      <td>
                        {s.trial_period_end
                          ? new Date(s.trial_period_end).toLocaleDateString('he-IL')
                          : ''}
                      </td>
                      <td className='fs-8'>{s.ad_name || s.source}</td>
                      <td>{s.connected ? '✅' : <span className='text-danger'>לא</span>}</td>
                      <td>{s.revenue ? ils(s.revenue) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default AdsPage
